import { ChangePasswordSuccessIcon, LockSquareIcon } from '../../../../../assets/icons';
import { usePasswordValidation } from '../../../../../hooks/usePasswordValidation';
import { setChangePasswordModal } from '../../../../../store/workspace/slice';
import { changePasswordThunk } from '../../../../../store/auth/thunks';
import { PASSWORD_RULES } from '../../constants';
import { ValidationState } from '../../types';
import { useDispatch } from '../../../../../store/store';
import { useEffect, useState } from 'react';
import styles from './style.module.scss';
import Modal from '../../../../../components/Modal';
import Input from '../../../../../components/Input';

type Step = 'Form' | 'Done';

export default function ChangePassword() {
    const dispatch = useDispatch();
    const [step, setStep] = useState<Step>('Form');
    const [isLoading, setIsLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [oldPassword, setOldPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const { min, num, char, word } = usePasswordValidation(newPassword);
    /** Unmet rules only turn red once the user has tried to submit. */
    const [showFailures, setShowFailures] = useState(false);

    const rules = {
        min,
        num,
        char,
        word,
        match: Boolean(newPassword) && newPassword === confirmPassword,
    };
    const isValid = min && num && char && word && newPassword === confirmPassword;

    /** Any edit to the password clears the red state until the next attempt. */
    useEffect(() => {
        setShowFailures(false);
    }, [min, num, char, word, confirmPassword]);

    const ruleState = (met: boolean): ValidationState => {
        if (met) return 'Valid';
        return showFailures ? 'Invalid' : 'NotValidated';
    };

    const closeModal = () => dispatch(setChangePasswordModal(false));

    const next = () => {
        if (step === 'Done') {
            closeModal();
            return;
        }

        if (!isValid) {
            setShowFailures(true);
            return;
        }

        setIsLoading(true);
        setErrorMessage('');

        dispatch(changePasswordThunk({ oldPassword, newPassword }))
            .unwrap()
            .then(() => {
                setStep('Done');
            })
            .catch(() => {
                setErrorMessage('Old password is not correct.');
            })
            .finally(() => {
                setIsLoading(false);
            });
    };

    return (
        <Modal
            size='sm'
            closeFunc={closeModal}
            icon={<LockSquareIcon />}
            title='Change Login Password'
            childClassName={styles.wrapper}
            actions={step === 'Form' ? 'both' : 'confirm'}
            dismissButton={{
                text: 'Cancel',
                type: 'Outline-Primary',
                disabled: isLoading,
                onClick: closeModal,
            }}
            confirmButton={{
                text: step === 'Form' ? 'Confirm' : 'Ok',
                type: 'Primary',
                onClick: next,
                loading: isLoading,
            }}
        >
            {step === 'Form' && <div className={styles.form}>
                <Input
                    type='password'
                    value={oldPassword}
                    label='Old password'
                    onChange={setOldPassword}
                    hasError={Boolean(errorMessage)}
                />
                <Input
                    type='password'
                    value={newPassword}
                    label='New password'
                    onChange={setNewPassword}
                />
                <Input
                    type='password'
                    label='Confirm password'
                    value={confirmPassword}
                    onChange={setConfirmPassword}
                />
                <div className={styles.validation}>
                    {PASSWORD_RULES.map(rule => <span
                        key={rule.key}
                        className={styles.validation_item}
                        data-validation-status={ruleState(rules[rule.key])}
                    >
                        {rule.text}
                    </span>)}
                </div>
            </div>}

            {step === 'Done' && <div className={styles.success}>
                <ChangePasswordSuccessIcon />
                <span>Your password has been changed successfully.</span>
            </div>}

            {errorMessage && <span className={styles.error}>{errorMessage}</span>}
        </Modal>
    );
};
